import * as XLSX from "xlsx";
import { importText } from "./mdfImportMatching";

export const isImageFile = (fileName = "") =>
  /\.(jpg|jpeg|png|jfif|webp)$/i.test(fileName.toLowerCase());

export const formatDate = (dateString, language, fallback) =>
  dateString ? new Date(dateString).toLocaleDateString(language) : fallback;

export const formatFileSize = (bytes = 0) => {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  if (bytes < 1024 * 1024 * 1024)
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  return (bytes / (1024 * 1024 * 1024)).toFixed(1) + " GB";
};

export const parseComandaLivrare = async (file) => {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const ws = wb.Sheets[wb.SheetNames[0]];
  if (!ws || !ws["!ref"]) return { items: [], colorHint: "", millingHint: "" };

  const rows=XLSX.utils.sheet_to_json(ws,{header:1,defval:""});
  const hintAfter=label=>{
    for(const row of rows.slice(0,60)) {
      const i=row.findIndex(v=>importText(v).includes(label));
      if(i>=0)return String(row.slice(i+1).find(v=>String(v).trim())??"").trim();
    }
    return "";
  };
  const millingHint = hintAfter("tip frezare") || String(ws["C17"]?.v ?? "").trim();
  const colorHint = hintAfter("culoare") || String(ws["E17"]?.v ?? "").trim();
  const headerIndex=rows.findIndex(row=>row.some(v=>/^(inaltime|lungime)/.test(importText(v)))&&row.some(v=>importText(v).startsWith("latime")));
  const header=headerIndex>=0?rows[headerIndex].map(importText):[];
  const column=(pattern,fallback)=>{const i=header.findIndex(v=>pattern.test(v));return i<0?fallback:i;};
  const shiftedHeader=column(/^(inaltime|lungime)/,1)===0;
  const heightColumn=shiftedHeader?1:column(/^(inaltime|lungime)/,1);
  const widthColumn=shiftedHeader?2:column(/^latime/,2);
  const quantityColumn=shiftedHeader?3:column(/^cantitate/,3);
  const observationColumn=shiftedHeader?5:column(/observatii/,5);
  const rng = XLSX.utils.decode_range(ws["!ref"]);
  const num = (value) => {
    const number = Number((value ?? "").toString().replace(",", "."));
    return isFinite(number) ? number : 0;
  };
  const items = [];

  for (let row = headerIndex>=0?headerIndex+1:20; row <= rng.e.r; row++) {
    const cellNr = ws[XLSX.utils.encode_cell({ r: row, c: 0 })]?.v;
    const length = num(ws[XLSX.utils.encode_cell({ r: row, c: heightColumn })]?.v);
    const width = num(ws[XLSX.utils.encode_cell({ r: row, c: widthColumn })]?.v);
    const quantity = num(ws[XLSX.utils.encode_cell({ r: row, c: quantityColumn })]?.v) || 1;
    const details = ws[XLSX.utils.encode_cell({ r: row, c: observationColumn })]?.v;

    if (!(num(cellNr)>0) || !(length>0&&width>0)) continue;

    items.push({
      size: [length, width, 18],
      qty: quantity,
      details: String(details ?? "").trim(),
      kant: ["-", 0, "-", 0],
      rotable: true,
      position: [0, 0, 0],
      rotation: [0, 0, 0],
      colorHint,
      codFrezare: millingHint,
      milling: millingHint,
      importMaterial: "MDF"
    });
  }

  return { items, colorHint, millingHint };
};

export const detectExcelTemplate = (ws) => {
  const cell17A = ws["A17"]?.v;
  if ((cell17A && importText(cell17A).includes("tip frezare")) || XLSX.utils.sheet_to_json(ws,{header:1}).slice(0,60).some(row=>row.some(v=>importText(v).includes("tip frezare")))) {
    return "comanda-livrare";
  }

  const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
  const normalizedRows = data.map((row) =>
    (row || []).map((cell) =>
      String(cell || "")
        .toLowerCase()
        .trim()
    )
  );
  const headerRow = normalizedRows.find(
    (row) =>
      row.some((cell) => cell.includes("cod pal")) &&
      row.some((cell) => cell.includes("lungime")) &&
      row.some((cell) => cell.includes("latime"))
  );

  if (!headerRow) return "unknown";

  const hasCodPal = headerRow.some((header) => header.includes("cod pal"));
  const hasLungime = headerRow.some((header) => header.includes("lungime"));
  const hasLatime = headerRow.some((header) => header.includes("latime"));
  const hasMaterial = headerRow.some((header) => header.includes("material"));
  const hasLength = headerRow.some((header) => header.includes("length"));

  if (hasCodPal && hasLungime && hasLatime) return "cutting-template";
  if (hasMaterial && hasLength) return "second-template";
  return "unknown";
};

export const parseCmrPreview = (xmlText) => {
  const doc = new DOMParser().parseFromString(xmlText, "application/xml");
  const statsNode = doc.querySelector("stats");
  const stats = statsNode
    ? {
        parts: statsNode.querySelector("parts")?.textContent,
        usedParts: statsNode.querySelector("used_parts")?.textContent,
        usedSheets: statsNode.querySelector("used_sheets")?.textContent,
        partArea: statsNode.querySelector("part_area")?.textContent,
        avgYield: statsNode.querySelector("average_yield")?.textContent,
        lastYield: statsNode.querySelector("last_sheet_yield")?.textContent,
        cutLength: statsNode.querySelector("cut_length")?.textContent,
        bandingLength: statsNode.querySelector("banding_length")?.textContent
      }
    : null;
  const sheets = Array.from(doc.querySelectorAll("tab_sheets sheet")).map(
    (sheet, index) => ({
      id: index + 1,
      length: sheet.querySelector("len")?.textContent,
      width: sheet.querySelector("wid")?.textContent,
      qty: sheet.querySelector("qty")?.textContent,
      material: sheet.querySelector("descr")?.textContent
    })
  );
  const parts = Array.from(doc.querySelectorAll("tab_parts part")).map(
    (part, index) => {
      const getValue = (node) => node?.getAttribute("val") || "-";
      return {
        id: index + 1,
        length: part.querySelector("len")?.textContent,
        width: part.querySelector("wid")?.textContent,
        qty: part.querySelector("qty")?.textContent,
        comment: part.querySelector("ini")?.textContent?.trim() || "",
        rotatable: part.querySelector("rot")?.textContent === "1",
        kantL1: getValue(part.querySelector("bl1")),
        kantL2: getValue(part.querySelector("bl2")),
        kantW1: getValue(part.querySelector("bw1")),
        kantW2: getValue(part.querySelector("bw2"))
      };
    }
  );
  return { stats, sheets, parts };
};

export const parseXlsxExport = async (file) => {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const ws = wb.Sheets[wb.SheetNames[0]];
  if (!ws || !ws["!ref"]) return { items: [], colorHint: "" };

  const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  const range = XLSX.utils.decode_range(ws["!ref"]);
  const normalize = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/\u00a0/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  const headerRowIndex = data.findIndex((row) => {
    const normalized = (row || []).map(normalize);
    return (
      normalized.some((cell) => cell.includes("cod pal")) &&
      normalized.some((cell) => cell.includes("lungime")) &&
      normalized.some((cell) => cell.includes("latime")) &&
      normalized.some((cell) => cell.includes("buc"))
    );
  });

  if (headerRowIndex < 0) return { items: [], colorHint: "" };

  const subHeaderRowIndex = headerRowIndex + 1;
  const dataStartRow = headerRowIndex + 2;
  const top = {};
  const sub = {};
  for (let column = range.s.c; column <= range.e.c; column++) {
    top[column] = normalize(data[headerRowIndex]?.[column]);
    sub[column] = normalize(data[subHeaderRowIndex]?.[column]);
  }
  const findColumn = (predicate) => {
    for (let column = range.s.c; column <= range.e.c; column++) {
      if (predicate(top[column], sub[column])) return column;
    }
    return -1;
  };
  const colColor = findColumn((topCell) => topCell.includes("cod pal"));
  const colLength = findColumn((topCell) => topCell.includes("lungime"));
  const colWidth = findColumn((topCell) => topCell.includes("latime"));
  const colQuantity = findColumn((topCell) => topCell.includes("buc"));
  const colDupl = findColumn((topCell) => topCell.includes("duplung"));
  const colRot = findColumn((topCell) => topCell.includes("rotabil"));
  const colObs = findColumn((topCell) => topCell.includes("observatii"));
  const start04 = findColumn(
    (topCell) =>
      topCell.includes("cant abs") &&
      (topCell.includes("0,4") || topCell.includes("0.4"))
  );
  const start2mm = findColumn(
    (topCell) => topCell.includes("cant abs") && topCell.includes("2 mm")
  );
  const start1 = findColumn((topCell) => topCell === "cant 1");
  const start42 = findColumn((topCell) => topCell === "cant 42");
  const cell = (row, column) => {
    const value = ws[XLSX.utils.encode_cell({ r: row, c: column })]?.v;
    return value == null ? "" : String(value).trim();
  };
  const num = (value) => {
    const number = Number(String(value ?? "").replace(",", "."));
    return Number.isFinite(number) ? number : 0;
  };
  const pickCount = (row, start) => {
    if (start < 0) return [0, 0];
    return [num(cell(row, start)), num(cell(row, start + 1))];
  };
  const items = [];

  for (let row = dataStartRow; row <= range.e.r; row++) {
    const length = num(cell(row, colLength));
    const width = num(cell(row, colWidth));
    const quantity = num(cell(row, colQuantity)) || 1;
    const colorName = colColor >= 0 ? cell(row, colColor) : "";
    const dupl = colDupl >= 0 ? cell(row, colDupl) : "";
    const obs = colObs >= 0 ? cell(row, colObs) : "";
    const rotable = (colRot >= 0 ? num(cell(row, colRot)) : 1) === 1;
    if (!length && !width && !quantity && !colorName && !obs && !dupl) continue;

    const [long04, short04] = pickCount(row, start04);
    const [long2, short2] = pickCount(row, start2mm);
    const [long1, short1] = pickCount(row, start1);
    const [long42, short42] = pickCount(row, start42);
    let kantType = "-";
    let longCount = 0;
    let shortCount = 0;
    if (long04 + short04 > 0) {
      kantType = "04";
      longCount = long04;
      shortCount = short04;
    } else if (long2 + short2 > 0) {
      kantType = "2";
      longCount = long2;
      shortCount = short2;
    } else if (long1 + short1 > 0) {
      kantType = "1";
      longCount = long1;
      shortCount = short1;
    } else if (long42 + short42 > 0) {
      kantType = "42";
      longCount = long42;
      shortCount = short42;
    }
    if (!length && !width) continue;
    items.push({
      size: [length, width, 18],
      qty: quantity,
      details: obs || dupl || "",
      kant: [kantType, longCount, shortCount],
      rotable,
      position: [0, 0, 0],
      rotation: [0, 0, 0],
      colorHint: colorName
    });
  }
  return { items, colorHint: "" };
};

export const parseSecondExcelTemplate = async (file) => {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(ws);
  const items = rows.map((row) => ({
    size: [Number(row.length), Number(row.width), 18],
    qty: Number(row.qty || 1),
    details: row.comment || "",
    kant: ["-", 0, 0],
    rotable: true,
    position: [0, 0, 0],
    rotation: [0, 0, 0]
  }));
  return { items, colorHint: "" };
};

const normalizeCantType = (raw) => {
  const value = String(raw ?? "")
    .trim()
    .toLowerCase();
  if (!value || value === "-") return "-";
  if (/(^|[^0-9])0\s*[.,]?\s*4([^0-9]|$)/.test(value) || /\b04\b/.test(value))
    return "04";
  if (/\b2(\s*mm)?\b/.test(value)) return 2;
  if (/\b1(\s*mm)?\b/.test(value)) return 1;
  if (/\b42(\s*mm)?\b/.test(value)) return 42;
  return value;
};

export const parseCmrXml = (xmlText) => {
  const doc = new DOMParser().parseFromString(xmlText, "application/xml");
  const sheetDescriptions = Array.from(
    doc.querySelectorAll("tab_sheets sheet descr")
  )
    .map((node) => (node.textContent || "").trim())
    .filter(Boolean);
  const colorHint = sheetDescriptions[0] || "";
  const items = Array.from(doc.querySelectorAll("tab_parts part")).map(
    (part) => {
      const length = Number(part.querySelector("len")?.textContent || 0);
      const width = Number(part.querySelector("wid")?.textContent || 0);
      const qty = Number(part.querySelector("qty")?.textContent || 1);
      const details = (part.querySelector("ini")?.textContent || "").trim();
      const rotable = Number(part.querySelector("rot")?.textContent || 0) === 0;
      const values = ["bl1", "bl2", "bw1", "bw2"].map((selector) =>
        normalizeCantType(
          part.querySelector(selector)?.getAttribute("val") || "-"
        )
      );
      const isOn = (value) => value !== "-" && value !== "" && value != null;
      const longCount = values.slice(0, 2).filter(isOn).length;
      const shortCount = values.slice(2).filter(isOn).length;
      const type = values.find(isOn) || "-";
      return {
        size: [length, width, 18],
        qty,
        details,
        kant: [type, longCount, shortCount],
        rotable,
        position: [0, 0, 0],
        rotation: [0, 0, 0]
      };
    }
  );
  return { items, colorHint };
};

export const parseCsv = (csvText) => {
  const lines = csvText.split(/\r?\n/).filter((line) => line.trim().length);
  if (!lines.length) return { items: [], colorHint: "" };
  const separator = lines[0].includes(";") ? ";" : ",";
  const headers = lines[0]
    .split(separator)
    .map((header) => header.trim().toLowerCase());
  const index = (name) => headers.findIndex((header) => header === name);
  const lengthIndex = index("len") >= 0 ? index("len") : index("length");
  const widthIndex = index("wid") >= 0 ? index("wid") : index("width");
  const quantityIndex = index("qty") >= 0 ? index("qty") : index("quantity");
  const detailsIndex = index("details");
  const rotableIndex = index("rotable");
  const rotationIndex = index("rot");
  const colorIndex = index("color");
  const items = lines.slice(1).map((row) => {
    const cells = row.split(separator).map((cell) => cell.trim());
    const length = Number(cells[lengthIndex] || 0);
    const width = Number(cells[widthIndex] || 0);
    const qty = Number(cells[quantityIndex] || 1);
    let rotable = true;
    if (rotationIndex >= 0) {
      rotable = Number(cells[rotationIndex] || 0) === 0;
    } else if (rotableIndex >= 0) {
      rotable = /^(1|true|igen)$/i.test(cells[rotableIndex] || "");
    }
    return {
      size: [length, width, 0],
      qty,
      details: detailsIndex >= 0 ? cells[detailsIndex] : "",
      rotable,
      colorHint: colorIndex >= 0 ? cells[colorIndex] : ""
    };
  });
  const hints = Array.from(
    new Set(items.map((item) => (item.colorHint || "").toLowerCase()))
  ).filter(Boolean);
  return { items, colorHint: hints.length === 1 ? hints[0] : "" };
};

export const toKantString = (value) => {
  if (value == null || value === "") return "[-,0,0]";
  let array = null;
  if (Array.isArray(value)) {
    array = value;
  } else if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) array = parsed;
    } catch {
      const text = value.replace(/^\s*\[/, "").replace(/\]\s*$/, "");
      array = text.split(",").map((item) => item.trim());
    }
  }
  if (!array || array.length < 3) return "[-,0,0]";
  if (array.length === 4) {
    const longCount = Number(array[1]) || 0;
    const shortCount = Number(array[3]) || 0;
    if (longCount && shortCount && String(array[0]) !== String(array[2])) {
      throw new Error("Eltérő hossz- és szélességi kanttípus nem menthető egy elemre.");
    }
    array = [longCount ? array[0] : shortCount ? array[2] : "-", longCount, shortCount];
  }
  let type = (array[0] ?? "").toString().trim();
  type = type.replace(/^("(.*)"|'(.*)')$/, "$2$3").replace(/[\[\]]/g, "");
  return `[${type},${Number(array[1]) || 0},${Number(array[2]) || 0}]`;
};

export const normalizeItemPayload = (payload) => {
  const allowed = new Set([
    "itemId",
    "name",
    "details",
    "material",
    "kant",
    "qty",
    "position",
    "rotation",
    "size",
    "tablePosition",
    "tableRotation",
    "rotable",
    "painted",
    "milling",
    "millingVariantId",
    "color",
    "object",
    "work"
  ]);
  const normalized = Object.fromEntries(
    Object.entries(payload).filter(([key]) => allowed.has(key))
  );
  const toArrayString = (value) => {
    if (value == null) return null;
    if (Array.isArray(value)) return JSON.stringify(value);
    const text = String(value);
    return text.trim().startsWith("[") && text.trim().endsWith("]")
      ? text
      : text;
  };
  if ("size" in normalized)
    normalized.size = toArrayString(normalized.size ?? [0, 0, 0]);
  if ("position" in normalized)
    normalized.position = toArrayString(normalized.position ?? [0, 0, 0]);
  if ("rotation" in normalized)
    normalized.rotation = toArrayString(normalized.rotation ?? [0, 0, 0]);
  if ("tablePosition" in normalized && normalized.tablePosition != null)
    normalized.tablePosition = toArrayString(normalized.tablePosition);
  if ("tableRotation" in normalized && normalized.tableRotation != null)
    normalized.tableRotation = toArrayString(normalized.tableRotation);
  if ("kant" in normalized)
    normalized.kant = toKantString(normalized.kant ?? ["-", 0, 0]);
  if (!("rotable" in normalized)) normalized.rotable = true;
  if (
    "milling" in normalized &&
    (normalized.milling == null || normalized.milling?.millingId == null)
  )
    delete normalized.milling;
  if (normalized.color && normalized.color.colorId == null)
    normalized.color = null;
  return normalized;
};
